import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useGetDeliveryPricingsQuery, useEditDeliveryPricingMutation } from 'store/api/deliveryApi';
import { type IDeliveryPricing, DeliveryPricingRouteType } from 'types/IDeliveryPricing';
import { Button, ButtonState, ButtonType } from 'components/button/Button';
import { useNotification } from 'components/notification/Notification';

const routeTypeLabels: Record<DeliveryPricingRouteType, string> = {
  [DeliveryPricingRouteType.OfficeToOffice]: 'Office → Office',
  [DeliveryPricingRouteType.OfficeToAddress]: 'Office → Address',
  [DeliveryPricingRouteType.AddressToOffice]: 'Address → Office',
  [DeliveryPricingRouteType.AddressToAddress]: 'Address → Address'
};

interface IEditDialogProps {
  pricing: IDeliveryPricing
  isOpen: boolean
  onClose: () => void
}

const EditDialog = ({ pricing, isOpen, onClose }: IEditDialogProps) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [editPricing, { isLoading }] = useEditDeliveryPricingMutation();
  const [priceEur, setPriceEur] = useState(String(pricing.priceEur));
  const [effectiveFromDate, setEffectiveFromDate] = useState(
    pricing.effectiveFromDate.substring(0, 10)
  );

  const handleSave = async () => {
    try {
      await editPricing({
        id: pricing.id,
        priceEur: Number(priceEur),
        effectiveFromDate: new Date(effectiveFromDate).toISOString()
      }).unwrap();
      showNotification(t('administration.deliveryPricings.saveSuccess'));
      onClose();
    } catch {
      showNotification(t('administration.deliveryPricings.saveError'));
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('administration.deliveryPricings.editTitle')} — {routeTypeLabels[pricing.routeType]} ≤{pricing.maxWeightKg} kg
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField
          label={t('administration.deliveryPricings.priceEur')}
          type="number"
          value={priceEur}
          onChange={(e) => { setPriceEur(e.target.value); }}
          inputProps={{ step: '0.01', min: '0' }}
          fullWidth
        />
        <TextField
          label={t('administration.deliveryPricings.effectiveFrom')}
          type="date"
          value={effectiveFromDate}
          onChange={(e) => { setEffectiveFromDate(e.target.value); }}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button
          type={ButtonType.secondary}
          text={t('common.cancel')}
          onClick={onClose}
        />
        <Button
          type={ButtonType.primary}
          text={t('common.save')}
          onClick={() => { void handleSave(); }}
          state={isLoading ? ButtonState.disabled : ButtonState.enabled}
        />
      </DialogActions>
    </Dialog>
  );
};

const AdminDeliveryPricingsTab = () => {
  const { t } = useTranslation();
  const { data: pricings } = useGetDeliveryPricingsQuery();
  const [editingPricing, setEditingPricing] = useState<IDeliveryPricing | null>(null);

  return (
    <div>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('administration.deliveryPricings.routeType')}</TableCell>
            <TableCell align="right">{t('administration.deliveryPricings.maxWeight')}</TableCell>
            <TableCell align="right">{t('administration.deliveryPricings.priceEur')}</TableCell>
            <TableCell>{t('administration.deliveryPricings.effectiveFrom')}</TableCell>
            <TableCell align="center">{t('administration.deliveryPricings.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pricings?.map((pricing) => (
            <TableRow key={pricing.id}>
              <TableCell>{routeTypeLabels[pricing.routeType]}</TableCell>
              <TableCell align="right">{pricing.maxWeightKg} kg</TableCell>
              <TableCell align="right">€{pricing.priceEur.toFixed(2)}</TableCell>
              <TableCell>{pricing.effectiveFromDate.substring(0, 10)}</TableCell>
              <TableCell align="center">
                <Tooltip title={t('administration.deliveryPricings.editTitle')}>
                  <IconButton size="small" onClick={() => { setEditingPricing(pricing); }}>
                    <EditIcon fontSize="small"/>
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {editingPricing && (
        <EditDialog
          pricing={editingPricing}
          isOpen={true}
          onClose={() => { setEditingPricing(null); }}
        />
      )}
    </div>
  );
};

export default AdminDeliveryPricingsTab;
